import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { climbsService } from '../../services/climbs';
import { climbVideosService } from '../../services/climb-videos';
import { localStorageService } from '../../services/localStorage';
import { useAuth } from '../../hooks/useAuth';
import { ClimbInstagramCard } from './ClimbInstagramCard';
import { FeedVideoCard } from './FeedVideoCard';
import { climbUrl } from '../../utils/climb-url';
import { toggleLikeLocal } from '../../utils/climb-video-like';
import type { Climb } from '../../types/climb';
import type { ClimbVideo } from '../../types/climb-video';

const FEED_SCROLL_KEY = 'climbs_feed_scroll_y';
const CACHE_KEY = 'home_climbs_cache';
const CACHE_TTL = 15 * 60 * 1000;

// Set to true when the module first loads after a browser reload.
// Cleared after the first component mount so SPA back-navigation still uses the cache.
let forceRefreshOnMount =
  typeof performance !== 'undefined' &&
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CachedClimbs {
  climbs: Climb[];
  page: number;
  hasMore: boolean;
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

// Mirrors the instagram feed card (FeedVideoCard / ClimbInstagramCard): a 3:4 media
// block above a like/share/time footer, in a single centered column.
function FeedSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="mx-auto w-full max-w-md overflow-hidden rounded-lg border bg-card md:max-w-lg">
          <div className="aspect-[3/4] w-full animate-pulse bg-muted" />
          <div className="flex items-center gap-4 px-3 py-3">
            <div className="h-5 w-10 animate-pulse rounded bg-muted" />
            <div className="h-5 w-10 animate-pulse rounded bg-muted" />
            <div className="ml-auto h-4 w-16 animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ClimbMasonryGrid() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const currentUserId = user ? String(user.id) : null;

  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const memoryCache = useRef<CachedClimbs | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = useRef(false);

  // Mirror page/hasMore so background revalidation can persist the cache without
  // recreating its callback (and going stale) on every pagination change.
  const pageRef = useRef(page);
  const hasMoreRef = useRef(hasMore);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  // ── Feed videos ───────────────────────────────────────────────────────────────
  // Update a climb's feed_video in state + cache so engagement survives feed navigation.
  const updateFeedVideo = useCallback((climbId: string, video: ClimbVideo | null) => {
    setClimbs(prev => {
      const next = prev.map(c => (c.id === climbId ? { ...c, feed_video: video } : c));
      const data: CachedClimbs = { climbs: next, page: pageRef.current, hasMore: hasMoreRef.current };
      memoryCache.current = data;
      localStorageService.set<CachedClimbs>(CACHE_KEY, data, CACHE_TTL);
      return next;
    });
  }, []);

  // Refresh engagement (likes/shares) for already-displayed climbs when the feed is
  // served from cache, so likes added after the cache was written still show up.
  // Only feed_video is merged in place — order, pagination and scroll are untouched.
  const revalidateEngagement = useCallback(() => {
    climbsService.searchClimbsWithMeta({
      scope: 'followed',
      page: 1,
      per_page: 24,
    }).then((result) => {
      const freshById = new Map(result.climbs.map(c => [c.id, c.feed_video ?? null]));
      setClimbs(prev => {
        let changed = false;
        const next = prev.map(c => {
          if (!freshById.has(c.id)) return c;
          changed = true;
          return { ...c, feed_video: freshById.get(c.id) ?? null };
        });
        if (!changed) return prev;
        const data: CachedClimbs = { climbs: next, page: pageRef.current, hasMore: hasMoreRef.current };
        memoryCache.current = data;
        localStorageService.set<CachedClimbs>(CACHE_KEY, data, CACHE_TTL);
        return next;
      });
    }).catch(() => { /* background refresh — ignore errors */ });
  }, []);

  // Open the video on the climb's own route (/.../video/:id). `fromFeed` tells the
  // climb page to show a "DivePeaks → back to the feed" navbar instead of the gym one;
  // `startTime` resumes playback where the feed preview left off (no restart on open).
  const handleVideoOpen = useCallback((climb: Climb, startTime: number) => {
    const video = climb.feed_video;
    if (!video) return;
    sessionStorage.setItem(FEED_SCROLL_KEY, String(window.scrollY));
    navigate(`${climbUrl(climb)}/video/${video.id}`, { state: { fromFeed: true, startTime } });
    climbVideosService.registerView(climb.slug, video.id).catch(() => {});
  }, [navigate]);

  const handleVideoLike = useCallback((climb: Climb) => {
    const video = climb.feed_video;
    if (!video || !user) return;
    updateFeedVideo(climb.id, toggleLikeLocal(video, user));
    climbVideosService.toggleLike(climb.slug, video.id)
      .then(updated => updateFeedVideo(climb.id, updated))
      .catch(() => updateFeedVideo(climb.id, video));
  }, [user, updateFeedVideo]);

  const handleVideoShare = useCallback((climb: Climb) => {
    const video = climb.feed_video;
    // A share counts once per user: skip if already shared (the share sheet still opened).
    if (!video || !user || video.shared_by_me) return;
    updateFeedVideo(climb.id, { ...video, shares_count: video.shares_count + 1, shared_by_me: true });
    climbVideosService.registerShare(climb.slug, video.id)
      .then(updated => updateFeedVideo(climb.id, updated))
      .catch(() => updateFeedVideo(climb.id, video));
  }, [user, updateFeedVideo]);

  // Restore the feed scroll position when returning from a video (back navigation).
  useEffect(() => {
    if (navigationType !== 'POP') return;
    const saved = sessionStorage.getItem(FEED_SCROLL_KEY);
    if (!saved) return;
    sessionStorage.removeItem(FEED_SCROLL_KEY); // one-shot
    requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
  }, [navigationType]);

  // Fetch / restore the feed on mount.
  useEffect(() => {
    const skipCache = forceRefreshOnMount;
    forceRefreshOnMount = false;

    if (!skipCache) {
      const mem = memoryCache.current;
      if (mem) {
        setClimbs(mem.climbs);
        setPage(mem.page);
        setHasMore(mem.hasMore);
        setLoading(false);
        setError(null);
        revalidateEngagement();
        return;
      }

      const cached = localStorageService.get<CachedClimbs>(CACHE_KEY);
      if (cached) {
        memoryCache.current = cached;
        setClimbs(cached.climbs);
        setPage(cached.page);
        setHasMore(cached.hasMore);
        setLoading(false);
        setError(null);
        revalidateEngagement();
        return;
      }
    } else {
      memoryCache.current = null;
    }

    setClimbs([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    setLoading(true);

    climbsService.searchClimbsWithMeta({
      scope: 'followed',
      page: 1,
      per_page: 24,
    }).then((result) => {
      const data: CachedClimbs = {
        climbs: result.climbs,
        page: 1,
        hasMore: result.meta?.has_more ?? false,
      };
      memoryCache.current = data;
      localStorageService.set<CachedClimbs>(CACHE_KEY, data, CACHE_TTL);
      setClimbs(result.climbs);
      setPage(1);
      setHasMore(data.hasMore);
    }).catch((err) => {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }).finally(() => {
      setLoading(false);
    });
  }, [revalidateEngagement]);

  // Load more pages
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const result = await climbsService.searchClimbsWithMeta({
        scope: 'followed',
        page: nextPage,
        per_page: 24,
      });
      setClimbs((prev) => {
        const updated = [...prev, ...result.climbs];
        const data: CachedClimbs = {
          climbs: updated,
          page: nextPage,
          hasMore: result.meta?.has_more ?? false,
        };
        memoryCache.current = data;
        localStorageService.set<CachedClimbs>(CACHE_KEY, data, CACHE_TTL);
        return updated;
      });
      setPage(nextPage);
      setHasMore(result.meta?.has_more ?? false);
    } catch {
      // silent
    } finally {
      isLoadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, page]);

  const loadMoreCbRef = useRef(loadMore);

  // Keep the callback ref fresh without recreating the observer on every loadMore change
  useEffect(() => { loadMoreCbRef.current = loadMore; });

  // Infinite scroll sentinel — rebuilt only when hasMore or loading changes
  // (loading: true→false is when the sentinel div first appears in the DOM)
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreCbRef.current(); },
      { threshold: 0.1 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  if (loading) {
    return <FeedSkeleton />;
  }

  if (error) {
    return (
      <Card><CardContent className="p-4 text-center text-muted-foreground">{t('home.exploreGrid.error')}</CardContent></Card>
    );
  }

  if (climbs.length === 0) {
    return (
      <Card><CardContent className="p-8 text-center text-muted-foreground">{t('home.exploreGrid.empty')}</CardContent></Card>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {climbs.map(climb => (
          climb.feed_video
            ? <FeedVideoCard
                key={climb.id}
                climb={climb}
                currentUserId={currentUserId}
                onOpen={handleVideoOpen}
                onLike={handleVideoLike}
                onShare={handleVideoShare}
              />
            : <ClimbInstagramCard key={climb.id} climb={climb} />
        ))}
      </div>

      {hasMore && (
        <div ref={loadMoreRef} className="py-4">
          {loadingMore && <FeedSkeleton count={1} />}
        </div>
      )}
    </>
  );
}
